import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Checkbox } from '@naf-simba/elements-checkbox';
import { useDispatch } from 'react-redux';
import { ExpandMore } from '@styled-icons/material-outlined';
import useSelector from '../../../../redux/typedHooks';
import { actions as carFilterActions } from '../../../../redux/modules/carFilter';
import {
  Container,
  DropdownButton,
  DropdownMenu,
  ModelHeader,
  SeasonHeader,
  VariantsContainer,
  VariantLabel,
  StyledChevron,
  SelectedVariantBanner,
  SelectedVariantsContainer,
  CloseButton,
  BrandName,
  SearchBar,
  StyledButtonContainer,
  StyledButton,
  Background,
} from './Styles';
import { TestedVehicle, TestedVehicleModel } from '../../../../../../types/TestedVehicle';
import { getSeasonName, getVariantId } from '../utils';

interface TestedVehicleVariantSelectProps {
  placeholder?: string;
  variants: TestedVehicle[];
  allSeasons?: boolean;
}

export const TestedVehicleVariantSelect = ({
  placeholder = 'Velg biler…',
  variants,
  allSeasons = true,
}: TestedVehicleVariantSelectProps) => {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const dispatch = useDispatch();

  const selectedIds = useSelector((state) => state.carFilter.selectedVariants) || [];

  const models = useMemo(() => {
    const modelMap = new Map<string, TestedVehicleModel>();
    const processedIds = new Set<string>();

    (variants ?? []).forEach((vehicle) => {
      if (!vehicle?.Brand || !vehicle?.Model || !vehicle?.VariantId || !vehicle?.TestedYear || !vehicle?.TestedSeason)
        return;

      const uniqueId = getVariantId(vehicle);
      if (processedIds.has(uniqueId)) return;
      processedIds.add(uniqueId);

      const key = `${vehicle.Brand}-${vehicle.Model}`;
      if (!modelMap.has(key)) {
        modelMap.set(key, {
          brand: vehicle.Brand,
          model: vehicle.Model,
          variants: [],
        });
      }

      const model = modelMap.get(key)!;
      const season = vehicle.TestedSeason.toLowerCase() === 'summer' ? 'Sommer' : 'Vinter';

      const variantExists = model.variants.some(
        (v) => v.id === uniqueId || (v.id.split('-')[0] === vehicle.VariantId && v.testedYear === vehicle.TestedYear),
      );
      if (variantExists) return;

      model.variants.push({
        id: uniqueId,
        name: vehicle.Name,
        season,
        year: Number(vehicle.Year),
        testedYear: Number(vehicle.TestedYear),
      });
    });

    return Array.from(modelMap.values());
  }, [variants]);

  const [expandedModels, setExpandedModels] = useState<Record<string, boolean>>({});

  useEffect(() => {
    const onClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClickOutside);
    return () => document.removeEventListener('mousedown', onClickOutside);
  }, []);

  useEffect(() => {
    if (open) {
      const dropdownButton = containerRef.current?.querySelector('button');
      if (dropdownButton) {
        const y = dropdownButton.getBoundingClientRect().top + window.scrollY - 80;
        window.scrollTo({ top: y, behavior: 'smooth' });
      }
    }
  }, [open]);

  const toggleModel = (key: string) => {
    setExpandedModels((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const handleVariantToggle = (id: string) => {
    if (!id) return;
    const newSelected = selectedIds.includes(id) ? selectedIds.filter((v) => v !== id) : [...selectedIds, id];
    dispatch(carFilterActions.setSelectedVariants(newSelected));
  };

  const compareBrandThenModel = useMemo(
    () => (x: TestedVehicleModel, y: TestedVehicleModel) => {
      const brandComparison = x?.brand?.localeCompare(y?.brand);
      if (brandComparison !== 0) {
        return brandComparison;
      }
      return x?.model?.localeCompare(y?.model);
    },
    [],
  );

  const sortedModels = useMemo(() => [...models].sort(compareBrandThenModel), [models, compareBrandThenModel]);

  const displayText =
    selectedIds?.length > 0 ? `${selectedIds.length} bil${selectedIds.length > 1 ? 'er' : ''} valgt` : placeholder;

  const getVariantBannerTitle = (modelID: string) => {
    if (!modelID || !variants?.length) return null;

    const [variantId, testedYear] = modelID.split('-');
    const variant = variants.find((v) => v?.VariantId === variantId && v?.TestedYear?.toString() === testedYear);
    if (!variant?.Brand || !variant?.Model) return null;

    const season = getSeasonName(variant.TestedSeason);

    return (
      <div>
        <BrandName>
          {variant.Brand} {variant.Model}
        </BrandName>
        {`${variant.Name ? ' ' : ''}${variant.Name.trim()}, testet ${season} ${variant.TestedYear}`}
      </div>
    );
  };

  const filteredModels = useMemo(() => {
    const searchLower = searchTerm?.toLowerCase();
    return sortedModels.filter(
      (model) =>
        model?.brand?.toLowerCase().includes(searchLower) ||
        model?.model?.toLowerCase().includes(searchLower) ||
        model?.variants?.some((variant) => variant?.name?.toLowerCase().includes(searchLower)),
    );
  }, [sortedModels, searchTerm]);

  const seasons = useMemo(() => {
    if (!models?.length) return [];
    const availableSeasons = new Set(models.flatMap((m) => m.variants.map((v) => v.season)));
    return allSeasons ? ['Sommer', 'Vinter'] : Array.from(availableSeasons);
  }, [models, allSeasons]);

  return (
    <Background>
      <Container ref={containerRef}>
        <DropdownButton onClick={() => setOpen(!open)}>
          <span>{displayText}</span>
          <StyledChevron $isOpen={open}>
            <ExpandMore size={24} />
          </StyledChevron>
        </DropdownButton>
        {open ? (
          <DropdownMenu ref={dropdownRef}>
            <SearchBar
              type="text"
              placeholder="Søk etter modell eller variant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e)}
            />
            {(filteredModels ?? []).map((m) => {
              if (!m?.brand || !m?.model) return null;
              const modelKey = `${m.brand}-${m.model}`;
              return (
                <div key={modelKey}>
                  <ModelHeader
                    onClick={() => toggleModel(modelKey)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ') {
                        e.preventDefault();
                        toggleModel(modelKey);
                      }
                    }}
                  >
                    {m.brand} {m.model}
                    <StyledChevron $isOpen={expandedModels[modelKey]}>
                      <ExpandMore size={24} />
                    </StyledChevron>
                  </ModelHeader>

                  {expandedModels[modelKey] && (
                    <VariantsContainer>
                      {seasons?.map((season) => {
                        const filtered =
                          m.variants
                            ?.filter((v) => v?.season === season)
                            ?.sort((a, b) => (b?.testedYear ?? 0) - (a?.testedYear ?? 0))
                            ?.filter((v, index, self) => index === self.findIndex((t) => t?.id === v?.id)) ?? [];

                        return (
                          <div key={modelKey + season}>
                            <SeasonHeader>{season}</SeasonHeader>
                            <VariantsContainer>
                              {filtered.length ? (
                                filtered.map((v) => (
                                  <VariantLabel key={v?.id}>
                                    <Checkbox
                                      checked={selectedIds?.includes(v?.id ?? '')}
                                      onChange={() => handleVariantToggle(v?.id ?? '')}
                                    />
                                    {v?.name || 'Variantnavn mangler'}, testet {v?.testedYear}
                                  </VariantLabel>
                                ))
                              ) : (
                                <VariantLabel>Ingen testdata for {season.toLowerCase()}</VariantLabel>
                              )}
                            </VariantsContainer>
                          </div>
                        );
                      })}
                    </VariantsContainer>
                  )}
                </div>
              );
            })}
            <StyledButtonContainer>
              <StyledButton onClick={() => setOpen(false)}>Se resultater ({selectedIds?.length ?? 0})</StyledButton>
            </StyledButtonContainer>
          </DropdownMenu>
        ) : null}

        <SelectedVariantsContainer>
          {selectedIds?.length > 0
            ? selectedIds
                .filter((id, index, self) => self.indexOf(id) === index)
                .map((id) => {
                  const title = getVariantBannerTitle(id);
                  if (!title) return null;
                  return (
                    <SelectedVariantBanner key={id}>
                      {title}
                      <CloseButton size={24} onClick={() => handleVariantToggle(id)} />
                    </SelectedVariantBanner>
                  );
                })
            : null}
        </SelectedVariantsContainer>
      </Container>
    </Background>
  );
};
